import React, { useEffect, useState, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Grid, Card, CircularProgress } from "@mui/material";
import axiosInstance from "api/axiosInstance";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { LanguageContext } from "../../../../context/LanguageContext";
import { COLORS, TYPO } from "theme/tokens";

// 打勾 icon（Sean 指定 PNG，路徑含空白需 encode）
const CHECK_ICON = "/images/icon/Check%20icon.png";

// 2026-05-25 新版卡片設計（依 Sean 截圖一/三）
// - 小額 tier (usdAmount <= 50): 用 MAS_Coin 2 1.png
// - 大額 tier (usdAmount > 50): 用 MAS_Coin 2 2.png
// - 中間卡橘色框 + 「最受歡迎」badge
// - 折扣最深的卡 + 「最優惠 (X% OFF)」紅 badge
export default function CoinRechargeTable({ tier }) {
  const [allPlans, setAllPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);

  useEffect(() => {
    loadLanguageFile("CoinRechargeTable", "plan/CoinRechargeTable/CoinRechargeTable_lang");
  }, [language]);
  const LANGUAGE = useMemo(
    () => componentLanguageData.CoinRechargeTable || { Main: {}, Error: {} },
    [componentLanguageData]
  );

  useEffect(() => {
    axiosInstance
      .get("/coin/recharge-plans")
      .then((res) => setAllPlans(res.data))
      .catch((err) => console.error(LANGUAGE.Error.Content1, err))
      .finally(() => setLoading(false));
  }, [LANGUAGE.Error.Content1]);

  const handleRecharge = async (planId) => {
    try {
      const res = await axiosInstance.post("/coin/purchase", {
        coinRechargePlanId: planId,
      });
      const popup = window.open();
      popup.document.write(res.data);
    } catch (err) {
      console.error(LANGUAGE.Error.Content2, err);
    }
  };

  // 依 tier 篩選
  const plans = useMemo(() => {
    const filtered = allPlans.filter((p) =>
      tier === "large" ? p.usdAmount > 50 : p.usdAmount <= 50
    );
    return filtered.sort((a, b) => a.usdAmount - b.usdAmount);
  }, [allPlans, tier]);

  // tier 對應 coin 圖
  const coinImageSrc =
    tier === "large" ? "/images/coin/MAS_Coin%202%202.png" : "/images/coin/MAS_Coin%202%201.png";

  // 找折扣最深的 plan (BadgeBest)
  const bestDiscountId = useMemo(() => {
    if (!plans.length) return null;
    return plans.reduce((best, p) => (p.discountRate > (best?.discountRate ?? -1) ? p : best), null)?.id;
  }, [plans]);

  // 中間那張 = 最受歡迎
  const popularId = plans[Math.floor(plans.length / 2)]?.id ?? null;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={3} alignItems="stretch">
      {plans.map((plan) => {
        const hasDiscount = plan.discountRate > 0;
        const discountPercent = hasDiscount ? Math.round(plan.discountRate * 100) : 0;
        const originalPrice = hasDiscount
          ? Math.round(plan.usdAmount / (1 - plan.discountRate))
          : null;

        const isBest = plan.id === bestDiscountId && hasDiscount;
        const isPopular = plan.id === popularId;

        const baseCoin = plan.coinAmount - (plan.discountcoin || 0);
        const bonusCoin = plan.discountcoin || 0;
        // 可使用次數＝總 coin 數 ÷ 2（生成、分析各算這麼多次）。例：100+50=150 → 各 75 次
        const usesPerFeature = Math.floor(plan.coinAmount / 2);
        const useTimesText = (LANGUAGE.Main.UseTimes || "可使用約 X 次").replace("X", usesPerFeature);
        const coinUnit = LANGUAGE.Main.CoinUnit || "MAS Coin";
        // 優惠額度：有加贈 → 🎁 儲值X枚 + 加贈Y枚；無加贈 → 原價
        const discountValue =
          bonusCoin > 0
            ? (LANGUAGE.Main.DiscountTpl || "🎁 儲值 {base} 枚 + 加贈 {bonus} 枚")
                .replace("{base}", baseCoin)
                .replace("{bonus}", bonusCoin)
            : LANGUAGE.Main.OriginalPriceLabel || "原價";
        // 4 列：優惠額度 / 獲得 MAS Coin(總額) / 生成 / 分析
        const features = [
          { label: LANGUAGE.Main.FeatureDiscount || "優惠額度", value: discountValue },
          { label: LANGUAGE.Main.FeatureGetCoin || "獲得 MAS Coin", value: `${plan.coinAmount} ${coinUnit}` },
          { label: LANGUAGE.Main.FeatureGen || "AI 策略助理-生成", value: useTimesText },
          { label: LANGUAGE.Main.FeatureAnalyze || "AI 策略助理-分析", value: useTimesText },
        ];

        return (
          <Grid item xs={12} sm={6} md={4} key={plan.id} sx={{ display: "flex" }}>
            <Card
              sx={{
                p: 3,
                pt: 5,
                width: "100%",
                position: "relative",
                borderRadius: "16px",
                border: isPopular ? `2px solid ${COLORS.main}` : "1px solid #eef0f3",
                boxShadow: isPopular
                  ? "0 8px 24px rgba(243,109,38,0.15)"
                  : "0 4px 12px rgba(0,0,0,0.04)",
                display: "flex",
                flexDirection: "column",
                transition: "all 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 32px rgba(0,0,0,0.08)",
                },
              }}
            >
              {/* 徽章：最優惠(紅 #D63A26)靠左、最受歡迎(橘 #F36D26)靠右 */}
              {isBest && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 12,
                    left: 12,
                    px: "16px",
                    py: "4px",
                    borderRadius: "2px",
                    background: COLORS.red,
                    ...TYPO.title,
                    fontWeight: 500,
                    color: COLORS.white,
                    lineHeight: 1.2,
                  }}
                >
                  {LANGUAGE.Main.BadgeBest} ({discountPercent}% OFF)
                </Box>
              )}
              {isPopular && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    px: "16px",
                    py: "4px",
                    borderRadius: "2px",
                    background: COLORS.main,
                    ...TYPO.bodyS,
                    fontWeight: 400,
                    color: COLORS.white,
                    lineHeight: 1.2,
                  }}
                >
                  {LANGUAGE.Main.BadgePopular}
                </Box>
              )}

              {/* 硬幣圖 */}
              <Box textAlign="center" my={2}>
                <Box
                  component="img"
                  src={coinImageSrc}
                  alt="MAS Coin"
                  sx={{ height: 130, width: "auto", maxWidth: "100%" }}
                  onError={(e) => {
                    e.target.style.opacity = "0.3";
                  }}
                />
              </Box>

              {/* 價格區：US$X(displayL/#000) + 原價刪除線(displayM/#D63A26) */}
              <Box display="flex" alignItems="baseline" gap={1} mb={0.5}>
                <MDTypography sx={{ ...TYPO.displayL, fontWeight: 500, color: COLORS.black, lineHeight: 1 }}>
                  US${plan.usdAmount}
                </MDTypography>
                {originalPrice && (
                  <MDTypography
                    sx={{
                      ...TYPO.displayM,
                      fontWeight: 500,
                      color: COLORS.red,
                      textDecoration: "line-through",
                    }}
                  >
                    ${originalPrice}
                  </MDTypography>
                )}
              </Box>
              {/* 總 MAS Coin：title/500/#F36D26 */}
              <MDTypography sx={{ ...TYPO.title, fontWeight: 500, color: COLORS.main, mb: 2 }}>
                {plan.coinAmount} {coinUnit}
              </MDTypography>

              {/* feature 列（4 列） */}
              <Box sx={{ flex: 1, mb: 2 }}>
                {features.map((f, idx) => (
                  <Box
                    key={idx}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1}
                    sx={{
                      py: 0.8,
                      borderBottom: idx < features.length - 1 ? "1px solid #f5f5f5" : "none",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={0.8} sx={{ flexShrink: 0 }}>
                      <Box
                        component="img"
                        src={CHECK_ICON}
                        alt=""
                        sx={{ width: 16, height: 16 }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                      <MDTypography sx={{ ...TYPO.bodyM, fontWeight: 400, color: COLORS.grayDark, whiteSpace: "nowrap" }}>
                        {f.label}
                      </MDTypography>
                    </Box>
                    <MDTypography sx={{ ...TYPO.bodyM, fontWeight: 400, color: COLORS.black, textAlign: "right" }}>
                      {f.value}
                    </MDTypography>
                  </Box>
                ))}
              </Box>

              {/* CTA：立即儲值（實心橘 #F36D26 / 高48 / 圓角8 / title 白） */}
              <MDButton
                fullWidth
                onClick={() => handleRecharge(plan.id)}
                sx={{
                  height: 48,
                  px: "32px",
                  py: "12px",
                  borderRadius: "8px",
                  background: COLORS.main,
                  color: `${COLORS.white} !important`,
                  ...TYPO.title,
                  fontWeight: 500,
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": { background: COLORS.main, boxShadow: "none" },
                }}
              >
                {LANGUAGE.Main.Content5 || "立即儲值"}
              </MDButton>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}

CoinRechargeTable.propTypes = {
  tier: PropTypes.oneOf(["small", "large"]),
};

CoinRechargeTable.defaultProps = {
  tier: "small",
};
